import { useEffect, useRef, useState } from 'react'
import emailjs from '@emailjs/browser'


const EMAILJS_SERVICE_ID  = import.meta.env.VITE_EMAILJS_SERVICE_ID
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID
const EMAILJS_PUBLIC_KEY  = import.meta.env.VITE_EMAILJS_PUBLIC_KEY
const TURNSTILE_SITE_KEY  = import.meta.env.VITE_TURNSTILE_SITE_KEY

const INITIAL_FORM = { name: '', email: '', company: '', message: '' }

export default function DemoModal({ isOpen, onClose }) {
  const [form, setForm]     = useState(INITIAL_FORM)
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('idle') // idle | sending | success | error
  const [turnstileToken, setTurnstileToken] = useState(null)
  
  const containerRef = useRef(null)   // div Turnstile renders into
  const widgetIdRef  = useRef(null)  // Turnstile widget ID

  // Render the Turnstile widget when the modal opens
  useEffect(() => {
    if (!isOpen) return

    if (!TURNSTILE_SITE_KEY) {
      setErrors(prev => ({ ...prev, captcha: 'Captcha is not configured. Please contact support.' }))
      return
    }

    // Wait for the Turnstile script to be ready
    const renderWidget = () => {
      if (!containerRef.current || widgetIdRef.current !== null) return
      if (!window.turnstile || typeof window.turnstile.render !== 'function') return

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => {
            setTurnstileToken(token)
            setErrors(prev => ({ ...prev, captcha: '' }))
          },
          'expired-callback': () => {
            setTurnstileToken(null)
            window.turnstile.reset(widgetIdRef.current)
          },
          'error-callback': () => {
            setTurnstileToken(null)
            setErrors(prev => ({ ...prev, captcha: 'Security check failed. Please try again.' }))
          },
        })
      } catch {
        setErrors(prev => ({ ...prev, captcha: 'Security check unavailable. Please refresh and try again.' }))
      }
    }

    // turnstile.ready() waits for the script to load if it hasn't yet
    if (window.turnstile && typeof window.turnstile.ready === 'function') {
      window.turnstile.ready(renderWidget)
    } else {
      // Script not yet available — poll briefly
      const interval = setInterval(() => {
        if (window.turnstile && typeof window.turnstile.ready === 'function') {
          clearInterval(interval)
          window.turnstile.ready(renderWidget)
        }
      }, 100)
      return () => clearInterval(interval)
    }

    // Cleanup: remove widget when modal closes
    return () => {
      if (widgetIdRef.current !== null && window.turnstile && typeof window.turnstile.remove === 'function') {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [isOpen])

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const handleClose = () => {
    setForm(INITIAL_FORM)
    setErrors({})
    setStatus('idle')
    setTurnstileToken(null)
    if (widgetIdRef.current !== null) {
      window.turnstile.reset(widgetIdRef.current)
    }
    onClose()
  }

  const validate = () => {
    const e = {}
    if (!form.name.trim())                          e.name    = 'Name is required.'
    if (!form.email.trim())                         e.email   = 'Email is required.'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
                                                    e.email   = 'Enter a valid email.'
    if (!form.company.trim())                       e.company = 'Company is required.'
    if (!form.message.trim())                       e.message = 'Message is required.'
    if (!turnstileToken)                            e.captcha = 'Please complete the security check.'
    return e
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const validationErrors = validate()
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors)
      return
    }

    setStatus('sending')
    
    try {
      //Verify token server-side
      const verifyRes = await fetch('/api/verify-captcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: turnstileToken }),
      })

      if (!verifyRes.ok) {
        throw new Error('Captcha verification failed.')
      }

      //Send email via EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name:    form.name,
          from_email:   form.email,
          from_company: form.company,
          message:      form.message,
        },
        EMAILJS_PUBLIC_KEY
      )

      setStatus('success')
    } catch {
      setStatus('error')
      // Reset widget on failure so user can retry
      if (widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current)
        setTurnstileToken(null)
      }
    }
  }

  if (!isOpen) return null

  return (
    <div className="demo-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="demo-modal" role="dialog" aria-modal="true" aria-labelledby="demo-title">

        {/* ── Header ── */}
        <div className="demo-modal__header">
          <div className="demo-modal__header-icon">
            <i className="fas fa-calendar-alt"></i>
          </div>
          <div>
            <h2 id="demo-title" className="demo-modal__title">Book a Demo</h2>
            <p className="demo-modal__subtitle">We'll be in touch soon.</p>
          </div>
          <button className="demo-modal__close" onClick={handleClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* ── Success state ── */}
        {status === 'success' ? (
          <div className="demo-modal__success">
            <div className="demo-modal__success-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h3>Request Sent!</h3>
            <p>Thanks, <strong>{form.name}</strong>. We'll reach out to <strong>{form.email}</strong> shortly.</p>
            <button className="btn btn-demo" onClick={handleClose}>Close</button>
          </div>
        ) : (
          <form className="demo-modal__form" onSubmit={handleSubmit} noValidate>

            {/* Name */}
            <div className={`demo-field ${errors.name ? 'demo-field--error' : ''}`}>
              <label htmlFor="demo-name">Full Name</label>
              <div className="demo-field__input-wrap">
                <i className="fas fa-user"></i>
                <input
                  id="demo-name"
                  name="name"
                  type="text"
                  placeholder="Jane Smith"
                  value={form.name}
                  onChange={handleChange}
                  autoComplete="name"
                />
              </div>
              {errors.name && <span className="demo-field__error">{errors.name}</span>}
            </div>

            {/* Email */}
            <div className={`demo-field ${errors.email ? 'demo-field--error' : ''}`}>
              <label htmlFor="demo-email">Work Email</label>
              <div className="demo-field__input-wrap">
                <i className="fas fa-envelope"></i>
                <input
                  id="demo-email"
                  name="email"
                  type="email"
                  placeholder="jane@company.com"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
              </div>
              {errors.email && <span className="demo-field__error">{errors.email}</span>}
            </div>

            {/* Company */}
            <div className={`demo-field ${errors.company ? 'demo-field--error' : ''}`}>
              <label htmlFor="demo-company">Company</label>
              <div className="demo-field__input-wrap">
                <i className="fas fa-building"></i>
                <input
                  id="demo-company"
                  name="company"
                  type="text"
                  placeholder="Company Inc."
                  value={form.company}
                  onChange={handleChange}
                  autoComplete="organization"
                />
              </div>
              {errors.company && <span className="demo-field__error">{errors.company}</span>}
            </div>

            {/* Message */}
            <div className={`demo-field ${errors.message ? 'demo-field--error' : ''}`}>
              <label htmlFor="demo-message">What would you like to see?</label>
              <div className="demo-field__input-wrap demo-field__input-wrap--textarea">
                <i className="fas fa-comment-dots"></i>
                <textarea
                  id="demo-message"
                  name="message"
                  rows={3}
                  placeholder="Tell us about your use case..."
                  value={form.message}
                  onChange={handleChange}
                />
              </div>
              {errors.message && <span className="demo-field__error">{errors.message}</span>}
            </div>

            {/* Turnstile widget — rendered into this div by the JS API */}
            <div className="demo-field">
              <div ref={containerRef} />
              {errors.captcha && <span className="demo-field__error">{errors.captcha}</span>}
            </div>

            {/* Error banner */}
            {status === 'error' && (
              <div className="demo-modal__error-banner">
                <i className="fas fa-exclamation-circle"></i>
                Something went wrong. Please try again or contact us directly.
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-demo demo-modal__submit"
              disabled={status === 'sending' || !turnstileToken}
            >
              {status === 'sending' ? (
                <><i className="fas fa-spinner fa-spin"></i><span>Sending…</span></>
              ) : (
                <><i className="fas fa-paper-plane"></i><span>Send Request</span></>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}